<template>
  <div class="c-bg--gray--pc c-common-footer-fixed">
    <div class="c-common-footer-fixed__inner">
      <TheHeader ref="theHeader" />
      <div class="c-common-inner">
        <TheTemplateJ
          :heading="thanksPointName"
          :indicated-item="['search', 'button']"
          :search-keyword="keyword"
          :error-message="error"
          @do-search="doSearch($event)"
        >
          <template #filter>
            <select v-model="filterType" class="p-message-filter">
              <option value="all">すべて</option>
              <option value="unread">未読あり</option>
            </select>
          </template>
          <template #contents>
            <LoadingContent v-if="isLoading" />
            <template v-else>
              <div class="p-the-page-j01">
                <div class="p-the-page-j01__body" ref="messageListContainer">
                  <JMessageArticleList
                    v-if="messageArticleList.length > 0"
                    :message-article-list="messageArticleList"
                    :exclude="[]"
                  />
                  <ThanksPointSearchNotFound
                    v-else-if="keyword"
                    :keyword="keyword"
                  />
                  <div
                    v-if="hasMore"
                    ref="loadMoreTrigger"
                    class="load-more-trigger"
                  />
                </div>
              </div>
            </template>
          </template>
        </TheTemplateJ>
      </div>
    </div>
    <TheFooter />
  </div>
</template>

<script lang="ts">
import TheTemplateJ from "@/general/components/templates/TheTemplateJ.vue";
import JMessageArticleList from "@/general/components/pages/parts/ThanksPointList/JMessageArticleList.vue";
import { Options, Vue } from "vue-class-component";
import commons from "@/common/store/modules/commons";
import { getThanksPointName } from "@/common/assets/js/utils/pointName";
import IconNoSearchResult from "@/common/components/icons/IconNoSearchResult.vue";
import globals from "@/general/store/modules/globals";
import store from "@/general/store";
import ThanksPointApi from "@/general/api/thanksPointApi";
import {
  ThanksPoint
} from "@/general/store/models";
import loginInfo from "@/general/store/modules/loginInfo";
import AuthApi from "@/general/api/authApi";
import auth from "@/general/store/modules/auth";
import LocalStorage from "@/general/utils/storage";
import CONST_DATA from "@/general/mixins/const/constData";
import ThanksPointSearchNotFound from "@/general/components/pages/parts/ThanksPointSearch/not-found.vue";
import { checkLengthInput } from "@/general/utils/helper";
import AuthStorage from "@/general/utils/auth";

@Options({
  props: {
    searchKeyword: { type: String, default: "" }
  },
  components: {
    TheTemplateJ,
    JMessageArticleList,
    IconNoSearchResult,
    ThanksPointSearchNotFound
  },
  store: store
})
export default class ThanksPointList extends Vue {
  searchKeyword!: string;
  ssoFlg = false;
  thanksPointList: any = [];
  currentPage = 1;
  keyword = "";
  isLoading = true;
  error = "";
  hasNextPage = true;
  isLoadingMore = false;
  observer: IntersectionObserver | null = null;
  apiKeyword = "";
  filterType = "all";

  breadcrumb: any = [
    {
      text: "",
      url: CONST_DATA.SITE_MAP.TOP_PAGE,
      pageType: "home"
    },
    {
      text: "サンクスメッセージ",
      url: "",
      pageType: "lower"
    }
  ];

  get isSp() {
    return globals.isLargeBreakpointWidthOfSp;
  }


  get pageSize() {
    return commons?.pageSize ? commons.pageSize : 10;
  }

  get messageArticleList() {
    let list = [...this.thanksPointList];
  
    if (this.filterType === "unread") {
      list = list.filter(
        (item: any) => item.unread_count > 0
      );
    }
    const filterKeywords = this.keywords.slice(1);
    if (filterKeywords.length === 0) {
      return list;
    }

    return list.filter((item: any) => {
      const target =
        [
          item.name,
          item.id,
          item.groupName,
          item.groupCode
        ]
        .join(" ")
        .toLowerCase();

      return filterKeywords.every(keyword =>
        target.includes(
          keyword.toLowerCase()
        )
      );
    });
  }

  get thanksFunctionEnabled() {
    return commons.thanksFunctionEnabled;
  }

  get messsageThreadEnabled() {
    return auth.user?.thread_enabled;
  }

  get thanksPointName() {
    return getThanksPointName(CONST_DATA.POINT_NAME.THANKS_POINT_DEFAULT_NAME2);
  }

  mounted() {
    AuthStorage.checkLogin();
  }

  get hasMore() {
    return this.hasNextPage;
  }

  async created() {
    this.ssoFlg = loginInfo.isSso;
    await commons.setOrgPageSpecificDataFromLocalStorage();

    const userDetails = LocalStorage.getItem("userDetails");
    const settings = userDetails ? JSON.parse(userDetails) : null;
    if (settings == null || settings.thanks_point_enabled === false) {
      window.location.href = "/home";
    }

    this.breadcrumb[1].text = "メッセージリスト";
    commons.setbreadcrumb(this.breadcrumb);
    await this.initThanksPointList();
    this.$nextTick(() => {
      this.setupObserver();
    });
    setTimeout(() => {
      this.isLoading = false;
      // サンクス機能が有効化されていない場合、エラーページに飛ばす。
      // 少し待たないとデータ取得に間に合わず、常に/system-error/ページに飛ばされてしまうため、setTimeoutの中に記述している
      if (this.thanksFunctionEnabled === false) {
        if (this.ssoFlg) {
          // SSOログイン時は行き止まりエラー画面へ
          window.location.href = "/session-time-out/";
        } else {
          LocalStorage.removeAll();
          AuthApi.getLogout();
          auth.remove();
          window.location.href = "/system-error/";
        }
        return;
      }
    }, 1000);
  }

  async initThanksPointList() {
    this.isLoading = true;
    this.thanksPointList = [];
    this.error = "";
    this.hasNextPage = true;

    const checkValidLength = checkLengthInput(this.keyword, 50);
    if (!checkValidLength?.isValid) {
      this.isLoading = false;
      this.error = checkValidLength?.error;
      return false;
    }

    try {
      const thanksPointList = await ThanksPointApi.getThankPointList(
        this.apiKeyword, this.currentPage
      );

      if (thanksPointList?.isError || thanksPointList?.status >= 400) {
        throw new Error();
      }

      this.thanksPointList =
        thanksPointList.results?.map(
          (resObj: ThanksPoint) =>
            this.createMessageArticle(
              resObj,
              new Map()
            )
        ) ?? [];

      if (this.thanksPointList.length < 20) {
        this.hasNextPage = false;
      }

      this.isLoading = false;

      if (this.thanksPointList.length === 0) {
        this.apiKeyword.length == 0 ? this.error = "メッセージ履歴はありません。" : this.error = "";
        return;
      }

      try {
        const userIds =
          thanksPointList.results?.map(
            (item: ThanksPoint) => item.toUserId
          ) ?? [];

        void ThanksPointApi
          .getThanksUsersInfo(userIds)
          .then(userInfoResponse => {
            const imageMap =
              new Map<number, string>(
                userInfoResponse.results?.map(
                  user => [
                    user.id,
                    user.user_image
                  ]
                ) ?? []
              );

          this.thanksPointList.forEach((item: any) => {
            item.icon.medium =
              imageMap.get(item.user_id) ?? "";
          });
        });
      } catch (e) {
        console.error(e);
      }
    } catch (e) {
      this.error = "メッセージ一覧の取得に失敗しました。";
      this.hasNextPage = false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle search
   *
   * @param {String} $event
   */
  async doSearch($event: string) {

    this.keyword = $event || "";

    this.currentPage = 1;
    this.hasNextPage = true;

    const checkValidLength =
      checkLengthInput(this.keyword.trim(), 50);

    if (!checkValidLength?.isValid) {
      this.error = checkValidLength?.error;
      return;
    }

    const newApiKeyword = this.keywords[0] ?? "";

    if (this.apiKeyword !== newApiKeyword) {
      this.error = "";
      this.thanksPointList = [];
      this.apiKeyword = newApiKeyword;
      await this.initThanksPointList();
    }

    this.observer?.disconnect();

    this.$nextTick(() => {
      this.setupObserver();
    });
  }

  async loadMore() {

    if (this.isLoadingMore || !this.hasNextPage) {
      return;
    }

    this.isLoadingMore = true;

    try {
      this.currentPage++;
      const response =
        await ThanksPointApi.getThankPointList(
          this.apiKeyword,
          this.currentPage
        );

      if (response?.isError || response?.status >= 400) {
        throw new Error();
      }

      const results = response.results ?? [];


      if (results.length === 0) {
        this.hasNextPage = false;
        return;
      }

      const articles =
        results.map(
          (item: ThanksPoint) => this.createMessageArticle(
            item,
            new Map()
          )
        );
      try {
        const userIds = results.map(
          (item: ThanksPoint) => item.toUserId
        );

        const userInfoResponse =
          await ThanksPointApi.getThanksUsersInfo(
            userIds
          );

        const imageMap =
          new Map<number, string>(
            userInfoResponse.results?.map(
              user => [
                user.id,
                user.user_image
              ]
            ) ?? []
          );

        articles.forEach((item: any) => {
          item.icon.medium =
            imageMap.get(item.user_id) ?? "";
        });
      
        this.thanksPointList.push(...articles);
      } catch (e) {
        console.error(e);
      }

      if (results.length < 20) {
        this.hasNextPage = false;
      }

      this.isLoadingMore = false;
    } catch (e) {
      this.currentPage--;
      this.hasNextPage = false;
      this.observer?.disconnect();
    } finally {
      this.isLoadingMore = false;
    }
  }

  setupObserver() {
    const target = this.$refs.loadMoreTrigger as HTMLElement;
    const root = this.$refs.messageListContainer as HTMLElement;

    if (!target || !root) {
      return;
    }

    this.observer =
      new IntersectionObserver(
        entries => {
          if (
            entries[0].isIntersecting &&
            this.hasNextPage &&
            !this.isLoadingMore
          ) {
            this.loadMore();
          }
        },
        {
          root
        }
      );

    this.observer.observe(target);
  }

  get keywords() {
    return this.keyword
      .trim()
      .split(/[\s　]+/)
      .filter(Boolean);
}

  private createMessageArticle(
    resObj: ThanksPoint,
    imageMap: Map<number, string>
  ) {
    return {
      id: resObj.toUsingEmployeeId,
      user_id: resObj.toUserId,
      name: resObj.toUserName,
      date: this.formatMessageDate(resObj.sendDate),
      content: resObj.lastMessage,
      url:
        `${CONST_DATA.SITE_MAP.THANKS_POINT_MESSAGES}` +
        `?id=${resObj.toUserId}`,
      icon: {
        original: "",
        large: "",
        medium:
          imageMap.get(resObj.toUserId)?? "",small: ""
      },
      groupName: resObj.toGroupName,
      groupCode: resObj.toGroupCode,
      point: {
        giftPoints: resObj.giftPoints,
        receivedPoints: resObj.receivedPoints
      },
      unread: resObj.unreadCount > 0,
      unread_count: resObj.unreadCount,
      isDeleted: resObj.isDeleted
    };
  }

  formatMessageDate(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();

    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();

    if (y === now.getFullYear()) {
      return `${m}/${d}`;
    }

    return `${y}/${m}/${d}`;
  }
}
</script>

<style lang="scss" scoped>
.p-the-page-j01 {
  @include pc {
    padding-top: rem(20);
  }
  @include sp {
    padding-top: rem(16);
  }
}
.load-more-trigger {
  height: 1px;
}
.p-message-filter {
  height: 40px;
  min-width: 110px;
  margin-left: 12px;
  padding: 0 12px;
  border: 1px solid #c8c8c8;
  border-radius: 2px;
  background: #fff;
}

@media (max-width: 767px) {
  .p-message-filter {
    min-width: 100px;
    margin-left: 8px;
  }
}
.p-message-filter {
  height: 40px;
  min-width: 110px;
  margin-left: 12px;
  padding: 0 12px;
  border: 1px solid #c8c8c8;
  border-radius: rem(4);
  background: #fff;

  flex: 0 0 90px;
  width: 90px;

  @include sp {
    width: rem(75);
    min-width: rem(75);
    margin-left: 0;
  }

  @media (max-width: 360px) {
    display: block;
    width: 100%;
    min-width: 100%;
    margin-top: rem(8);
    margin-left: 0;
  }
}
.p-the-page-j01__body {
  height: calc(90vh - 260px);

  overflow-y: auto;
  overflow-x: hidden;

  background: $common-white;
}
</style>
